import { useState } from 'react';
import { useAsync } from '@/lib/useAsync';
import { toast } from '@/components/ui/Toast';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type ClaudeCredentialStatus } from '@/lib/api';
import { ClaudeSetupTerminal } from '@/components/ClaudeSetupTerminal';
import { usePortalConfig } from './PortalLayout';

/* Bring-your-own-AI access. The hive subscription panel is live: the hive
 * owner connects the client's Claude subscription right here — the server
 * runs the token generation, so there is nothing to install. The per-user
 * key tier below is on the roadmap (the MCP layer); that section sets the
 * mental model now and is honest about what is live versus arriving. */
export function PortalConnectPage() {
  const config = usePortalConfig();
  // The credential endpoint doubles as the tier probe: owners (and staff) get
  // 200, everyone else 403. It drives the subscription panel AND the "(you)"
  // marker on the permissions table, so the owner never reads the base tier
  // as their own.
  const credential = useAsync(() => api.getClaudeCredential(), []);
  const isOwner = credential.status === 'success';

  return (
    <>
      <div className="portal-head">
        <h1>Use the hive from your own AI</h1>
        <p>Keep working in Claude, ChatGPT or whatever you already use. Your key decides what it can see.</p>
      </div>

      <HiveSubscriptionPanel status={credential} />

      <div className="portal-cols" style={{ marginTop: 14 }}>
        <div>
          <div className="portal-panel">
            <h4>
              How it works <span className="portal-staged">arriving with the access rollout</span>
            </h4>
            <ol className="portal-steps">
              <li>You get a personal hive key. It's yours alone; it works nowhere else.</li>
              <li>Add the {config.name} hive as a connector in your AI, one copy-paste.</li>
              <li>Ask it something. The answer comes from the hive, with sources.</li>
            </ol>
            <p className="portal-hint">
              Lose a device and the key is revoked in one click; everything stops working instantly. Nothing is stored
              on your machine.
            </p>
          </div>
        </div>
        <div>
          <div className="portal-panel">
            <h4>What each key can do</h4>
            <table className="perm">
              <tbody>
                <tr>
                  <th>Capability</th>
                  <th>Team member{!isOwner && <span style={{ color: 'var(--p-accent)' }}> · you</span>}</th>
                  <th>Team lead</th>
                  <th>Hive owner{isOwner && <span style={{ color: 'var(--p-accent)' }}> · you</span>}</th>
                </tr>
                <tr><td>Ask your department's agent</td><td className="yes">✓</td><td className="yes">✓</td><td className="yes">✓</td></tr>
                <tr><td>See your own outputs</td><td className="yes">✓</td><td className="yes">✓</td><td className="yes">✓</td></tr>
                <tr><td>See the whole department</td><td className="no">✕</td><td className="yes">✓</td><td className="yes">✓</td></tr>
                <tr><td>Trigger any agent in the hive</td><td className="no">✕</td><td className="no">✕</td><td className="yes">✓</td></tr>
                <tr><td>Manage people and keys</td><td className="no">✕</td><td className="yes">✓ dept</td><td className="yes">✓ all</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

/* The hive's own subscription: visible to the hive owner (and staff), hidden
 * from everyone else — regular members get a 403 from the API and never see
 * this panel. The minted token stays server-side. */
function HiveSubscriptionPanel({ status }: { status: ReturnType<typeof useAsync<ClaudeCredentialStatus>> }) {
  const [terminalOpen, setTerminalOpen] = useState(false);

  if (status.status === 'loading') return null;
  if (status.status === 'error') {
    // Not the owner: this panel is simply not their concern.
    if (status.error instanceof ApiError && status.error.status === 403) return null;
    return null;
  }
  const connected = status.data.connected;

  return (
    <div className="portal-panel" style={{ marginTop: 4 }}>
      <h4>
        The hive's subscription{' '}
        <span className="portal-staged" style={connected ? { color: 'var(--p-green)' } : undefined}>
          {connected ? 'connected' : 'not connected'}
        </span>
      </h4>
      {!terminalOpen ? (
        <>
          <p className="body">
            {connected
              ? `Your Claude subscription powers every agent in the hive.${
                  status.data.updatedAt ? ` Connected ${timeAgo(status.data.updatedAt)}.` : ''
                }`
              : 'The hive runs on your own Claude subscription. Connect it once and every agent uses it. Nothing to install.'}
          </p>
          <div style={{ marginTop: 12 }}>
            <button
              className={connected ? 'portal-btn-ghost' : 'portal-btn'}
              onClick={() => setTerminalOpen(true)}
            >
              {connected ? 'Replace the subscription' : 'Connect your subscription'}
            </button>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 10 }}>
          <p className="body" style={{ marginBottom: 8 }}>
            This is the live connection session. Click the sign-in link it shows, approve access in the new tab, then
            paste the code straight back into the terminal below.
          </p>
          <ClaudeSetupTerminal
            onConnected={() => {
              toast.success('Subscription connected', 'Every agent in the hive runs on it from now on.');
              setTerminalOpen(false);
              status.reload();
            }}
            onExit={() => setTerminalOpen(false)}
          />
          <div style={{ marginTop: 8 }}>
            <button className="portal-btn-ghost" onClick={() => setTerminalOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <p className="portal-hint">
        The session runs on your hive's own server; the credential is stored encrypted and never shown to anyone.
      </p>
    </div>
  );
}
